import { useState, useMemo } from "react";
import { AppContainer } from "@/components/layout/app-container";
import KanbanColumn from "@/components/tasks/kanban-column";
import AddTaskDialog from "@/components/tasks/add-task-dialog";
import EditTaskDialog from "@/components/tasks/edit-task-dialog";
import ViewTaskDialog from "@/components/tasks/view-task-dialog";
import { ITask } from "@/interfaces/dashboard.interface";
import { mockKanbanTasks } from "@/services/tasks/mock-data";
import { Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import InputSearch from "@/components/ui/input-search";

export function TaskManagement() {
  const [tasks, setTasks] = useState<ITask[]>(mockKanbanTasks);
  const [searchTerm, setSearchTerm] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ITask | null>(null);
  const [defaultColumn, setDefaultColumn] = useState<'todo' | 'doing' | 'done'>('todo');

  // Filter tasks by search term
  const filteredTasks = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return tasks;
    return tasks.filter(
      (task) =>
        task.taskName.toLowerCase().includes(term) ||
        task.description?.toLowerCase().includes(term) ||
        task.project.toLowerCase().includes(term) ||
        task.stage.toLowerCase().includes(term)
    );
  }, [tasks, searchTerm]);

  // Group tasks by column
  const todoTasks = filteredTasks.filter((task) => task.column === 'todo');
  const doingTasks = filteredTasks.filter((task) => task.column === 'doing');
  const doneTasks = filteredTasks.filter((task) => task.column === 'done');

  // Handle add task
  const handleAddTask = (newTask: Partial<ITask>) => {
    setTasks((prev) => [...prev, newTask as ITask]);
  };

  // Handle view task
  const handleViewTask = (task: ITask) => {
    setSelectedTask(task);
    setViewDialogOpen(true);
  };

  // Handle edit task
  const handleEditTask = (task: ITask) => {
    setSelectedTask(task);
    setEditDialogOpen(true);
  };

  // Handle update task
  const handleUpdateTask = (updatedTask: ITask) => {
    setTasks((prev) =>
      prev.map((task) => (task.id === updatedTask.id ? updatedTask : task))
    );
  };

  // Handle delete task
  const handleDeleteTask = (taskId: string) => {
    setTasks((prev) => prev.filter((task) => task.id !== taskId));
  };

  // Handle open add dialog from column
  const handleOpenAddDialog = (column: 'todo' | 'doing' | 'done') => {
    setDefaultColumn(column);
    setAddDialogOpen(true);
  };

  // Handle open add dialog from header
  const handleOpenAddDialogFromHeader = () => {
    setDefaultColumn('todo');
    setAddDialogOpen(true);
  };

  return (
    <AppContainer
      title="Gestão de Tarefas"
      description="Texto de Apoio da página"
      searchBar={
        <InputSearch 
          value={searchTerm}
          onChange={setSearchTerm} 
          positionIcon={"end"}
          className="text-sm"
          classNameContainer="bg-transparent"
        />
      }
      button={
        <Button
          onClick={handleOpenAddDialogFromHeader}
          className="bg-[#7846FF] hover:bg-[#6635E6] text-[#F5F2FF] h-[50px] px-6 flex items-center gap-2"
        >
          <Plus className="w-[24px] h-[24px]" />
          <span className="text-[18px] font-bold">Add nova tarefa</span>
        </Button>
      }
    >
      <div className="bg-[#15203C] rounded-[20px] px-[30px] py-[40px] flex flex-col gap-[30px]">
        {/* Kanban Columns */}
        <div className="flex gap-[98px] items-start">
          <KanbanColumn
            title="Para Fazer"
            tasks={todoTasks}
            onAddTask={() => handleOpenAddDialog('todo')}
            onEditTask={handleEditTask}
            onViewTask={handleViewTask}
          />

          <KanbanColumn
            title="Fazendo"
            tasks={doingTasks}
            onAddTask={() => handleOpenAddDialog('doing')}
            onEditTask={handleEditTask}
            onViewTask={handleViewTask}
          />

          <KanbanColumn
            title="Concluídas"
            tasks={doneTasks}
            onAddTask={() => handleOpenAddDialog('done')}
            onEditTask={handleEditTask}
            onViewTask={handleViewTask}
          />
        </div>
      </div>

      {/* Add Task Dialog */}
      <AddTaskDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddTask}
        defaultColumn={defaultColumn}
      />

      {/* View Task Dialog */}
      <ViewTaskDialog
        open={viewDialogOpen}
        task={selectedTask}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedTask(null);
        }}
        onUpdateTask={handleUpdateTask}
      />

      {/* Edit Task Dialog */}
      <EditTaskDialog
        open={editDialogOpen}
        task={selectedTask}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTask(null);
        }}
        onSubmit={handleUpdateTask}
        onDelete={handleDeleteTask}
      />
    </AppContainer>
  );
}

export default TaskManagement;
